import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { first, forkJoin, map, type Observable, of, switchMap } from 'rxjs';

import { type CanvasElementFrom, type CanvasElementId } from '@blaze-runtime/engine-domain';
import {
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findFirstNodeInWorkflowOfType, isGetNode } from '@blaze-shared/workflow-domain';

import { ElementsFacade } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import { RuntimeFacade } from '../../../+state/runtime';
import {
  AbstractAiElementHandler,
  addEventWorkflowWithConnection,
  addSourceWorkflow,
  type AiWorkflowFinalizationEntry,
  dispatchValueBindingRefresh,
  getElement,
  RecordActionsHandlerService,
  resolveBulkActions,
  type ResolvedRecordActions,
  resolveRecordActions,
  resolveRecordClick,
  upsertElement,
} from '../../../ai';
import { ContainerFieldFiltersApplierService } from '../../../ai/page/container-field-filters-applier.service';

import { toEngineTableWidth } from './table.appearance';
import { type TableContext, type TableResolvedContext } from './table.definition';
import { buildTableProps } from './table.prop-builders';

const TABLE_VISIBLE_FIELDS_REFRESH_TIMEOUT_MS = 10_000;

type ResolvedRowActions =
  ResolvedRecordActions<PropertiesConfigInputContainerItemUnifiedRowActionModel>;
type ResolvedBulkActions =
  ResolvedRecordActions<PropertiesConfigInputContainerItemUnifiedBulkActionModel>;

export class TableElementHandler extends AbstractAiElementHandler<'table'> {
  private readonly containerFieldFiltersApplier = inject(ContainerFieldFiltersApplierService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly recordActionsHandlerService = inject(RecordActionsHandlerService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  readonly type = 'table';

  prepare(context: TableContext): Observable<TableResolvedContext> {
    this.resolveIcons(context.element, context.element.rowActions, context.element.bulkActions);

    const workflowContext = this.resolveWorkflowContext(context);

    if (!workflowContext) {
      return of(context);
    }

    return of({
      ...context,
      workflowContext,
    });
  }

  process(context: TableResolvedContext, draft: WritableDraft<EngineState>): void {
    const { props: rowActionProps, workflows: rowActionWorkflows }: ResolvedRowActions =
      resolveRecordActions(
        context.element.id,
        context.element.rowActions,
        context.componentWorkflows,
        'RowActions',
      );
    const { props: bulkActionProps, workflows: bulkActionWorkflows }: ResolvedBulkActions =
      resolveBulkActions(
        context.element.id,
        context.element.bulkActions,
        context.componentWorkflows,
      );
    const resolvedRowClick = resolveRecordClick(
      context.element.id,
      context.element.rowClick,
      context.componentWorkflows,
      'RowClick',
    );

    const elementProps = buildTableProps(
      context,
      draft,
      rowActionProps,
      bulkActionProps,
      resolvedRowClick.props,
    );

    const result = upsertElement(draft, context, elementProps);
    const element = getElement(draft, result.elementId, 'table');
    const workflowsToFinalize: AiWorkflowFinalizationEntry[] = [];

    if (context.workflowContext) {
      addSourceWorkflow(draft, context.workflowContext.workflow);
    }

    const customActionWorkflows = [...rowActionWorkflows, ...bulkActionWorkflows];
    if (resolvedRowClick.workflow) {
      customActionWorkflows.push(resolvedRowClick.workflow);
    }

    customActionWorkflows.forEach((workflow) => {
      const { hasUpdates } = addEventWorkflowWithConnection(draft, context, workflow);
      workflowsToFinalize.push({ workflow, forceUpdate: hasUpdates });
    });

    context.workflowsToFinalize = workflowsToFinalize;

    element.width = toEngineTableWidth(context.element.width);
  }

  finalize(element: CanvasElementFrom<'table'>, context: TableResolvedContext): Observable<void> {
    const visibleFields = this.getVisibleFieldNames(context);
    const sourceCreation$ = context.workflowContext
      ? this.finalizeSourceWorkflow(element, context.workflowContext, undefined, visibleFields)
      : of(undefined);

    const updateWorkflows$ = this.aiWorkflowGenerationService.handleAiGeneratedWorkflows(
      context.workflowsToFinalize ?? [],
    );

    return sourceCreation$.pipe(
      switchMap(() => updateWorkflows$),
      switchMap(() => this.ensureSourceWorkflowSchemaResolvable(context)),
      switchMap(() => this.applyVisibleFields(element.id, visibleFields)),
      switchMap(() => this.applyFieldFilters(element.id, context)),
      switchMap(() =>
        forkJoin([
          this.recordActionsHandlerService.finalizeGeneratedPages(
            element.id,
            context.element.rowActions,
            context.element.rowClick,
          ),
          this.recordActionsHandlerService.finalizeDeleteWorkflow(
            element.id,
            context.element.rowActions,
          ),
          this.recordActionsHandlerService.finalizeBulkActionWorkflows(
            element.id,
            context.element.bulkActions,
          ),
          this.recordActionsHandlerService.finalizeAddNew(element.id, !!context.element.addNewRow),
        ]),
      ),
      map(() => undefined),
    );
  }

  private ensureSourceWorkflowSchemaResolvable(context: TableResolvedContext): Observable<void> {
    const workflow = context.workflowContext?.workflow;
    const datasetId = findFirstNodeInWorkflowOfType(workflow, isGetNode)?.datasetId;

    if (!workflow || !datasetId) {
      return of(undefined);
    }

    return forkJoin([
      this.runtimeFacade.workflows$.pipe(
        map((workflows) => workflows.some((registered) => registered.id === workflow.id)),
        first(Boolean),
      ),
      this.schemaService.get(datasetId).pipe(first(Boolean)),
    ]).pipe(map(() => undefined));
  }

  private getVisibleFieldNames(context: TableResolvedContext): string[] | undefined {
    return context.element.visibleFields?.map((field) => field.field);
  }

  private applyVisibleFields(
    elementId: CanvasElementId,
    visibleFields: string[] | undefined,
  ): Observable<void> {
    if (!visibleFields?.length) {
      return of(undefined);
    }

    return dispatchValueBindingRefresh({
      elementsFacade: this.elementsFacade,
      runtimeFacade: this.runtimeFacade,
      store: this.store,
      elementId,
      timeoutMs: TABLE_VISIBLE_FIELDS_REFRESH_TIMEOUT_MS,
      onTimeout: () =>
        console.warn(`Table element (id:${elementId}): value binding schema did not resolve`),
      onError: (error) =>
        console.warn(`Table element (id:${elementId}): refresh binding failed`, error),
      visibleFields,
    });
  }

  private applyFieldFilters(
    elementId: CanvasElementId,
    context: TableResolvedContext,
  ): Observable<void> {
    const datasetId = findFirstNodeInWorkflowOfType(
      context.workflowContext?.workflow,
      isGetNode,
    )?.datasetId;

    return this.containerFieldFiltersApplier.apply({
      elementId,
      elementType: 'table',
      fieldFilters: context.element.fieldFilters,
      datasetId,
    });
  }
}
